"use client";

// ============================================================================
// planet-view.tsx — planet core + sub-goal rings. Boards 05/06.
// Treatment A "Ink arc": dashed track, solid arc from 12 o'clock,
// complete = thin solid ring that stays.
//
// PASS-4 (web) — parity with iOS `PlanetView.swift`:
//   • `compact` drops the ring tracks and their padding so a planet
//     reads as a small world in orbit, not an activity dial (#510).
//   • per-skin surface treatment (PSS-1 Fix 5) so six worlds never
//     look like six copies of one gradient ball: ember cracks, glacier
//     fractures, rose cloud bands, tide waves, slate craters, and a
//     Saturn ring band behind brass. Seeded from the goal identity
//     with the same djb2 iOS uses, so a world keeps its craters.
// ============================================================================

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  type Goal,
  PLANET_SKIN,
  SUB_GOAL_INK,
  GalaxyColor,
  EASE_STANDARD,
  MOTION,
} from "./galaxy-core";

export interface PlanetViewProps extends React.HTMLAttributes<HTMLDivElement> {
  goal: Goal;
  /** planet core diameter, px */
  diameter: number;
  /** set to a ring index to play the one-shot unlock halo */
  pulsingRing?: number | null;
  /** No ring tracks, no ring padding — a world in orbit. */
  compact?: boolean;
}

/** djb2 over UTF-8 — the same hash iOS `PassportProgressView.stableHash` uses. */
export function stableHash(s: string): number {
  let h = 5381 >>> 0;
  const bytes = new TextEncoder().encode(s);
  for (const b of bytes) h = (((h << 5) >>> 0) + h + b) >>> 0;
  return h >>> 0;
}

/** Tiny deterministic PRNG (mulberry32) — one per planet, seeded from its identity. */
function seeded(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function PlanetView({
  goal,
  diameter,
  pulsingRing = null,
  compact = false,
  className,
  ...props
}: PlanetViewProps) {
  const reduceMotion = useReducedMotion();
  const pad = compact ? 6 : 12 + goal.subGoals.length * 9;
  const total = diameter + pad * 2;
  const c = total / 2;
  const gradId = React.useId();
  const clipId = `${gradId}-clip`;
  const [hi, base, lo] = PLANET_SKIN[goal.skin];
  const seed = React.useMemo(() => stableHash(goal.title + goal.worldName), [goal.title, goal.worldName]);

  return (
    <div
      className={cn("relative select-none", className)}
      style={{ width: total, height: total, minWidth: 44, minHeight: 44 }} // 44px min hit target
      {...props}
    >
      <svg viewBox={`0 0 ${total} ${total}`} className="absolute inset-0 overflow-visible">
        <defs>
          <radialGradient id={gradId} cx="38%" cy="32%" r="72%">
            <stop offset="0%" stopColor={hi} />
            <stop offset="52%" stopColor={base} />
            <stop offset="100%" stopColor={lo} />
          </radialGradient>
        </defs>

        {/* rings — order = list order, innermost first (never in compact mode) */}
        {!compact && goal.subGoals.map((sg, i) => {
          const r = diameter / 2 + 10 + i * 9;
          const circ = 2 * Math.PI * r;
          const ink = SUB_GOAL_INK[sg.type];
          return (
            <g key={sg.id}>
              <circle
                cx={c}
                cy={c}
                r={r}
                fill="none"
                stroke={`${GalaxyColor.ivory}21`}
                strokeWidth={1.4}
                strokeDasharray="2 5"
              />
              {sg.progress >= 1 ? (
                <circle cx={c} cy={c} r={r} fill="none" stroke={ink} strokeWidth={2.2} />
              ) : sg.progress > 0 ? (
                <motion.circle
                  cx={c}
                  cy={c}
                  r={r}
                  fill="none"
                  stroke={ink}
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${c} ${c})`}
                  initial={false}
                  animate={{ strokeDasharray: `${circ * sg.progress} ${circ}` }}
                  transition={{ duration: MOTION.ringDraw, ease: EASE_STANDARD }}
                />
              ) : null}
            </g>
          );
        })}

        {/* core */}
        <g
          style={
            goal.state === "locked"
              ? { filter: "saturate(0.15) brightness(0.55)" }
              : undefined
          }
        >
          {/* brass: Saturn-style ring band BEHIND the planet, tilted −18° */}
          {goal.skin === "brass" && (
            <ellipse
              cx={c}
              cy={c}
              rx={diameter * 0.85}
              ry={diameter * 0.17}
              fill="none"
              stroke={`url(#${gradId}-band)`}
              strokeWidth={Math.max(1.2, diameter * 0.055)}
              opacity={goal.state === "locked" ? 0.3 : 0.8}
              transform={`rotate(-18 ${c} ${c})`}
            />
          )}
          <circle cx={c} cy={c} r={diameter / 2} fill={`url(#${gradId})`} />
          {/* terminator shading */}
          <radialGradient id={`${gradId}-t`} cx="72%" cy="78%" r="60%">
            <stop offset="0%" stopColor="black" stopOpacity="0.5" />
            <stop offset="100%" stopColor="black" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${gradId}-band`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#EAC161" stopOpacity="0.55" />
            <stop offset="50%" stopColor="#C08A26" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#EAC161" stopOpacity="0.55" />
          </linearGradient>
          <clipPath id={clipId}>
            <circle cx={c} cy={c} r={diameter / 2} />
          </clipPath>
          {/* surface — clipped to the disc so nothing spills past the limb */}
          <g clipPath={`url(#${clipId})`}>
            <SkinSurface skin={goal.skin} seed={seed} c={c} d={diameter} />
          </g>
          <circle cx={c} cy={c} r={diameter / 2} fill={`url(#${gradId}-t)`} />
        </g>

        {/* reachable: orange pulse ring */}
        {goal.state === "reachable" && !reduceMotion && (
          <motion.circle
            cx={c}
            cy={c}
            r={diameter / 2 + 8}
            fill="none"
            stroke={`${GalaxyColor.orange}80`}
            strokeWidth={1}
            animate={{ scale: [1, 1.08, 1], opacity: [0.9, 0.3, 0.9] }}
            transition={{ duration: 2.6, ease: EASE_STANDARD, repeat: Infinity }}
            style={{ transformOrigin: `${c}px ${c}px` }}
          />
        )}

        {/* one-shot unlock halo (800ms, then never again) */}
        {pulsingRing != null && goal.subGoals[pulsingRing] && (
          <motion.circle
            cx={c}
            cy={c}
            r={diameter / 2 + 14 + pulsingRing * 9}
            fill="none"
            stroke={SUB_GOAL_INK[goal.subGoals[pulsingRing].type]}
            strokeWidth={1.5}
            initial={{ scale: 0.7, opacity: 0.8 }}
            animate={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: MOTION.unlockHalo, ease: EASE_STANDARD }}
            style={{ transformOrigin: `${c}px ${c}px` }}
          />
        )}
      </svg>

      {/* done glow */}
      {goal.state === "done" && (
        <div
          className="pointer-events-none absolute rounded-full"
          style={{
            inset: pad,
            boxShadow: `0 0 24px -4px ${GalaxyColor.orange}80`,
          }}
        />
      )}
    </div>
  );
}

// ── PSS-1 Fix 5 — per-skin surface treatment ────────────────────────────────
// Mirrors the iOS overlays one for one: same counts, same opacities,
// same seed, so a planet looks the same on the phone and on the web.
function SkinSurface({ skin, seed, c, d }: { skin: Goal["skin"]; seed: number; c: number; d: number }) {
  const r = d / 2;
  const x0 = c - r;
  const y0 = c - r;
  const paths = React.useMemo(() => {
    const out: { d: string; stroke: string; width: number; opacity: number }[] = [];
    if (skin === "ember") {
      const rng = seeded(seed);
      for (let k = 0; k < 3; k++) {
        const sx = x0 + d * (0.15 + rng() * 0.7);
        const sy = y0 + d * (0.15 + rng() * 0.7);
        let p = `M ${sx.toFixed(2)} ${sy.toFixed(2)}`;
        for (let j = 0; j < 4; j++) {
          const nx = sx + (rng() - 0.5) * d * 0.5;
          const ny = sy + (rng() - 0.5) * d * 0.5;
          p += ` L ${nx.toFixed(2)} ${ny.toFixed(2)}`;
        }
        out.push({ d: p, stroke: "#FFB27A", width: Math.max(0.6, d * 0.012), opacity: 0.3 });
      }
    } else if (skin === "glacier") {
      const rng = seeded(seed + 17);
      for (let k = 0; k < 5; k++) {
        const sx = x0 + d * rng();
        const sy = y0 + d * rng();
        let p = `M ${sx.toFixed(2)} ${sy.toFixed(2)}`;
        for (let j = 0; j < 3; j++) {
          const nx = sx + (rng() - 0.5) * d * 0.6;
          const ny = sy + (rng() - 0.5) * d * 0.6;
          p += ` L ${nx.toFixed(2)} ${ny.toFixed(2)}`;
        }
        out.push({ d: p, stroke: "#E8F2FF", width: Math.max(0.4, d * 0.008), opacity: 0.25 });
      }
    } else if (skin === "tide") {
      for (let i = 0; i < 4; i++) {
        const y = y0 + d * (0.25 + (i / 4) * 0.55);
        const amp = d * 0.02;
        const step = d / 12;
        let p = `M ${x0.toFixed(2)} ${y.toFixed(2)}`;
        for (let sIdx = 1; sIdx <= 12; sIdx++) {
          const x = x0 + step * sIdx;
          const phase = Math.sin(((sIdx + i * 0.7) * Math.PI) / 2.4) * amp;
          p += ` L ${x.toFixed(2)} ${(y + phase).toFixed(2)}`;
        }
        out.push({ d: p, stroke: "#CFEFF2", width: Math.max(0.6, d * 0.01), opacity: 0.3 });
      }
    }
    return out;
  }, [skin, seed, x0, y0, d]);

  if (skin === "ember") {
    return (
      <>
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill="none" stroke={p.stroke} strokeOpacity={p.opacity} strokeWidth={p.width} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {/* soft inner-ring glow */}
        <circle cx={c} cy={c} r={r - Math.max(1, d * 0.03) / 2} fill="none" stroke="#FF6A2E" strokeOpacity={0.25} strokeWidth={Math.max(1, d * 0.03)} style={{ filter: `blur(${d * 0.03}px)` }} />
      </>
    );
  }
  if (skin === "glacier" || skin === "tide") {
    return (
      <>
        {paths.map((p, i) => (
          <path key={i} d={p.d} fill="none" stroke={p.stroke} strokeOpacity={p.opacity} strokeWidth={p.width} strokeLinecap="round" />
        ))}
      </>
    );
  }
  if (skin === "rose") {
    // Jupiter-style cloud bands — three capsules wrapping the disc.
    return (
      <>
        {[0, 1, 2].map((i) => {
          const t = i / 3;
          const h = d * 0.1;
          return (
            <rect key={i} x={x0 - d * 0.025} y={y0 + d * (0.28 + t * 0.5) - h / 2} width={d * 1.05} height={h} rx={h / 2} fill="#F6E1D2" fillOpacity={0.3} />
          );
        })}
      </>
    );
  }
  if (skin === "slate") {
    // 3–5 craters, positions pinned by the seed.
    const s = seed >>> 0;
    const count = 3 + (s % 3);
    return (
      <>
        {Array.from({ length: count }, (_, i) => {
          const a = ((s + i * 7919) % 100) / 100;
          const b = ((s + i * 3313) % 100) / 100;
          const sz = ((s + i * 5323) % 100) / 100;
          const cx = x0 + d * (0.15 + a * 0.7);
          const cy = y0 + d * (0.15 + b * 0.7);
          const cd = d * (0.08 + sz * 0.12);
          return (
            <circle key={i} cx={cx} cy={cy} r={cd / 2} fill="black" fillOpacity={0.2} stroke="white" strokeOpacity={0.12} strokeWidth={0.5} />
          );
        })}
      </>
    );
  }
  return null;
}
